import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import partial from 'lodash/partial';
import uniq from 'lodash/uniq';

import {
    isPageNode,
    isParentNode,
    isSectionNode,
    Navigation,
    NavigationItem,
    NavigationItemType,
    NavigationListFilters,
    NavigationMap,
    NavigationMapItem,
} from '@lumapps/navigation/types';
import { cleanTranslatableObject, GLOBAL } from '@lumapps/translations';
import { generateUUID } from '@lumapps/utils/string/generateUUID';
import { isURLValid } from '@lumapps/utils/string/isUrl';

import { NODE_TEMP_ID_PREFIX } from '../constants';
import { NAVIGATION_ADMIN } from '../keys';
import { nodeIsEditable } from './nodes';

/**
 * List all page ids for the given page type in navigation recursive tree.
 */
export const getPageNodeIds = (pageType: NavigationItemType, node: NavigationItem | Navigation): string[] => {
    if (isParentNode(node)) {
        return uniq(flatMap(node.children, partial(getPageNodeIds, pageType)));
    }
    if (isPageNode(node) && node.pageType === pageType) {
        return [node.pageId as string];
    }
    return [];
};

/**
 * Returns true if at least one translation for the given navigation item is valid
 * @param navigationItem - item to be validated
 */
export const isNavigationItemTitleValid = (navigationItem: NavigationMapItem) => {
    const { title } = navigationItem;

    return title && title.translations ? Object.values(title.translations).some((value) => Boolean(value)) : false;
};

/**
 * Returns true if the visibility for the given navigation item is valid
 * @param navigationItem - item to be validated
 */
export const isNavigationItemVisibilityValid = (navigationItem: NavigationMapItem) => {
    const { visibleBy } = navigationItem;

    return visibleBy ? visibleBy.length > 0 : false;
};

interface IsNavigationItemValidOptions {
    /** Whether the external url should be valid */
    validateExternalUrl?: boolean;
}

/**
 * Returns true if the item can be saved or not.
 * @param navigationItem - item to be validated
 * @param options IsNavigationItemValidOptions
 */
export const isNavigationItemValid = (
    navigationItem: NavigationMapItem,
    options: IsNavigationItemValidOptions = {},
): boolean => {
    const { type } = navigationItem;
    const pageType = navigationItem.pageReference?.type;

    switch (type) {
        case NavigationItemType.SYSTEM_PAGE:
            return true;
        case NavigationItemType.SECTION:
            return isNavigationItemTitleValid(navigationItem) && isNavigationItemVisibilityValid(navigationItem);
        case NavigationItemType.LINK: {
            if (!navigationItem.url) {
                return false;
            }

            const hasValidUrl = options.validateExternalUrl ? isURLValid(navigationItem.url) : true;

            return (
                isNavigationItemTitleValid(navigationItem) &&
                isNavigationItemVisibilityValid(navigationItem) &&
                hasValidUrl
            );
        }
        case NavigationItemType.PAGE:
            switch (pageType) {
                case NavigationItemType.CONTENT:
                case NavigationItemType.COMMUNITY:
                case NavigationItemType.ARTICLE:
                    return (
                        Boolean(navigationItem.pageReference?.id) &&
                        Boolean(pageType) &&
                        isNavigationItemVisibilityValid(navigationItem)
                    );
                default:
                    return false;
            }
        default:
            return false;
    }
};

/**
 * Determines whether a given node can be moved to a given section, taking into consideration
 * width and depth rules. As for now, it only works with a depth of 3 levels. If we ever want to
 * change this depth, the logic needs to be changed to something a little bit smarter, something
 * that will recursively consider each level and do the math. We do not want to do this now, because
 * in the future we might remove this constraint.
 */
export const isMoveNodeToSectionAllowed = ({
    depth,
    section,
    node,
    maxDepth,
    maxWidth,
    depthLimitMessage,
    widthLimitMessage,
    editionRestrictionMessage,
}: {
    depth: number;
    section: NavigationMapItem;
    node?: NavigationMapItem;
    maxDepth?: number;
    maxWidth?: number;
    depthLimitMessage?: string;
    widthLimitMessage?: string;
    editionRestrictionMessage?: string;
}) => {
    if (!nodeIsEditable(section.partialPermission)) {
        return { isDisabled: true, message: editionRestrictionMessage };
    }

    if (node) {
        if (isSectionNode(node)) {
            /**
             * If the depth plus the current section is already over the max, we return immediately
             */
            if (maxDepth && depth + 1 >= maxDepth) {
                return { isDisabled: true, message: depthLimitMessage };
            }

            /**
             * If it is a section and there is one section inside this node, it means that moving
             * it to wherever the user wants to move it will add 2 levels. If the current depth
             * plus 2 levels goes over the max, we return with isDisabled: true.
             */
            const hasASectionAsChild = node.children?.filter((child) => isSectionNode(child));

            if (maxDepth && hasASectionAsChild.length > 0 && depth + 2 >= maxDepth) {
                return { isDisabled: true, message: depthLimitMessage };
            }
        }

        if (maxWidth && section.children?.length === maxWidth) {
            return { isDisabled: true, message: widthLimitMessage };
        }
    }

    return { isDisabled: false };
};

/**
 * Test if node are the same base on the node ID
 * @param currentNodeId The ID of the current node.
 * @param targetedNodeId The ID of the node we want to target.
 */
export const isSameNode = (currentNodeId?: string, targetedNodeId?: string) =>
    !isNil(currentNodeId) && currentNodeId === targetedNodeId;

/**
 * Test if a NavigationMapItem is invalid and indicates why
 * All sections, and subsections should contain at least one item and up to maxWidth items.
 */
export const checkForInvalidNavigationMapItem = (
    // eslint-disable-next-line default-param-last
    items: NavigationMapItem[] = [],
    maxWidth?: number,
): 'emptySection' | 'fullSection' | null =>
    items.reduce(
        (acc, curr) => {
            if (acc) {
                return acc;
            }
            if (curr.type && curr.type !== 'section') {
                return null;
            }
            if (!curr.children?.length) {
                return 'emptySection';
            }
            if (maxWidth && curr.children.length > maxWidth) {
                return 'fullSection';
            }

            return checkForInvalidNavigationMapItem(curr.children, maxWidth);
        },
        null as 'emptySection' | 'fullSection' | null,
    );

/**
 * Test if node can be edited
 * Article, Community and content node cannot be edited if showNavigationVisibility is false, because visibility cannot be set.
 */
export const canEditNode = (node: NavigationMapItem, showNavigationVisibility?: boolean): boolean =>
    !node?.pageReference?.type ||
    ![NavigationItemType.CONTENT, NavigationItemType.COMMUNITY, NavigationItemType.ARTICLE].includes(
        node.pageReference.type,
    ) ||
    showNavigationVisibility !== false;

/** Generate a new base Node with temp Id. Depending on the node type, we check by default or not "open in new tab" */
export const generateNewNodeBase = (nodeBase: Partial<NavigationMapItem>): Partial<NavigationMapItem> => ({
    id: generateUUID(NODE_TEMP_ID_PREFIX),
    ...nodeBase,
    // For nodes of type Link, `newTab` field is checked by default.
    newTab: nodeBase?.type === NavigationItemType.LINK || undefined,
});

/** Check if the given node has a temp Id */
export const isNodeTempId = (node: NavigationMapItem) => !node.id || node.id?.startsWith(NODE_TEMP_ID_PREFIX);

interface SanitizeNavigationMapOptions {
    removeEmptySectionChildren?: boolean;
}

/**
 * Utils function to sanitize a NavigationItem sent to backend when updating the navigationMap. It then recursively sanitize its children.
 *
 * **IMPORTANT NOTE**: If this function (and thus the API contract w/ backend) changes, please inform the owner of the site copy feature,
 * so they can also change their function on their side.
 */
const sanitizeNavigationMapChildrenForUpdate = (
    nodes: NavigationMapItem[],
    options: SanitizeNavigationMapOptions = {},
): NavigationMapItem[] => {
    return nodes.map((node: NavigationMapItem) => {
        const sanitizedChildren = node.children?.length ? sanitizeNavigationMapChildrenForUpdate(node.children) : [];

        return {
            id: isNodeTempId(node) ? undefined : node.id,
            title: node.type !== NavigationItemType.PAGE ? node.title : undefined,
            /** An empty node title could break the payload. */
            nodeTitle: cleanTranslatableObject(node.nodeTitle),
            type: node.type,
            icon: node.icon,
            newTab: node.newTab || undefined,
            hideTitle: node.hideTitle || undefined,
            properties: isEmpty(node.properties) ? undefined : node.properties,
            pageReference: node.pageReference?.id
                ? { id: node.pageReference.id, type: node.pageReference.type }
                : undefined,
            systemPageReference: node.systemPageReference?.type ? { type: node.systemPageReference.type } : undefined,
            url: node.type === NavigationItemType.LINK ? node.url : undefined,
            langs: node.langs || [],
            visibleBy: node.visibleBy,
            isHidden: node.isHidden,
            areChildrenHidden: node.areChildrenHidden,
            /**
             * For section and link nodes, the backend needs to keep slug info so it is not erased, as slugs for these
             * types of node are not stored by a content anymore.
             */
            slug:
                node.type && [NavigationItemType.SECTION, NavigationItemType.LINK].includes(node.type)
                    ? node.slug
                    : undefined,
            /**
             * The merged_uuid and legacyId fields are technical fields used by the backend
             * to manage nodes that have been created in the legacy app with the legacy api.
             * We need to keep them and send them back to the backend.
             * */
            merged_uuid: node.merged_uuid,
            legacyId: node.legacyId,
            children:
                sanitizedChildren.length === 0 &&
                (node.type !== NavigationItemType.SECTION || options.removeEmptySectionChildren)
                    ? undefined
                    : sanitizedChildren,
        };
    });
};

/** Utils function to sanitize the payload sent to backend when updating the navigationMap. It takes the whole NavigationMap as entry and returns only the necessary subset of it */
export const sanitizeNavigationMapForUpdate = (
    fullNavigationMap: NavigationMap,
    options: SanitizeNavigationMapOptions = {},
): NavigationMap => {
    return {
        id: fullNavigationMap.id,
        type: fullNavigationMap.type,
        version: fullNavigationMap.version,
        children: sanitizeNavigationMapChildrenForUpdate(fullNavigationMap.children, options),
    };
};

/**
 * Returns true if the navigation item props goes against the defined filters
 * @param navigationItem - item to be validated
 * @param filters - the filters set
 * @param defaultFeeds - the ids of the default user feeds set in LumApps (public / all)
 */
export const isNavigationItemFiltered = (
    navigationItem: Pick<NavigationMapItem, 'pageReference' | 'visibleBy' | 'langs'>,
    filters?: NavigationListFilters,
    defaultFeeds?: {
        allFeedId?: string;
        publicFeedId?: string;
    },
) => {
    /** if not filter is set, set it as not filtered */
    if (!filters) {
        return false;
    }

    /** Set the item as visible (not filtered) by default. */
    let isVisible = true;

    const { group: groupFilter, language: languageFilter } = filters;

    /** Filtering rules for the "group" filter  */
    if (groupFilter) {
        const { visibleBy = [], pageReference } = navigationItem;
        const { groupIds } = pageReference || {};

        /**
         * Reducer callback function used to check if the given group Id allows the node to be
         * visible with the current filter.
         */
        const visibilityReducer = (isVisible: boolean, groupId: string) => {
            /** If a previous group has granted visibility, no need to check further */
            if (isVisible) {
                return isVisible;
            }

            /** Whether the current group id matches the "public" feed id */
            const isPublic = groupId === defaultFeeds?.publicFeedId;
            /** Whether the current group id matches the "all" feed id or is publicly visible */
            const isVisibleByAll = isPublic || groupId === defaultFeeds?.allFeedId;
            /** Whether the current group id matches the filter */
            const filterIsInArray = groupId === groupFilter;

            switch (groupFilter) {
                case defaultFeeds?.publicFeedId:
                    return isPublic;
                case defaultFeeds?.allFeedId:
                    return isVisibleByAll;

                /**
                 * For all other cases, set the node as visible if is visible by all or if
                 * the group id matches the filter.
                 * */
                default:
                    return isVisibleByAll || filterIsInArray;
            }
        };

        /** Check whether the current node has the correct groups. */
        const nodeIsVisible = visibleBy.reduce<boolean>(visibilityReducer, false);

        /** If a page has groupIds, check whether they have the correct groups. */
        const pageIsVisible = groupIds ? groupIds.reduce<boolean>(visibilityReducer, false) : true;

        /** A node is visible if both the node and the linked page have the correct groups. */
        isVisible = nodeIsVisible && pageIsVisible;
    }

    /** Filtering rules for the "language" filter  */
    if (isVisible && languageFilter) {
        const { langs = [] } = navigationItem;
        // Node is visible if the lang array is empty or if it includes the requested language.
        isVisible = langs.length === 0 || langs.includes(languageFilter);
    }

    return !isVisible;
};

/** Get the type of content and helpers keys used in the add/edit dialog, depending on the page type / picker type. */
export const getNavigationItemModalContentKeys = ({
    type,
    isEdit,
}: {
    type?: NavigationItemType;
    isEdit?: boolean;
}) => {
    switch (type) {
        case NavigationItemType.CONTENT:
            return {
                // If isEdit, do not display the contentBlock title
                contentBlockTitle: isEdit ? '' : GLOBAL.CONTENTS,
                titleHelper: NAVIGATION_ADMIN.ITEM_TITLE_HELPER_CONTENT,
                visibilityHelper: NAVIGATION_ADMIN.ITEM_VISIBILITY_HELPER_CONTENT,
            };
        case NavigationItemType.COMMUNITY:
            return {
                contentBlockTitle: GLOBAL.COMMUNITY,
                titleHelper: NAVIGATION_ADMIN.ITEM_TITLE_HELPER_COMMUNITY,
                visibilityHelper: NAVIGATION_ADMIN.ITEM_VISIBILITY_HELPER_COMMUNITY,
            };
        case NavigationItemType.CUSTOM_LIST:
            return {
                contentBlockTitle: GLOBAL.CONTENT_LIST,
                titleHelper: NAVIGATION_ADMIN.ITEM_TITLE_HELPER_CUSTOM_LIST,
                visibilityHelper: NAVIGATION_ADMIN.ITEM_VISIBILITY_HELPER_CUSTOM_LIST,
            };
        case NavigationItemType.DIRECTORY:
            return {
                contentBlockTitle: GLOBAL.DIRECTORY,
                titleHelper: NAVIGATION_ADMIN.ITEM_TITLE_HELPER_DIRECTORY,
                visibilityHelper: NAVIGATION_ADMIN.ITEM_VISIBILITY_HELPER_DIRECTORY,
            };
        case NavigationItemType.USER_DIRECTORY:
            return {
                contentBlockTitle: GLOBAL.USER_DIRECTORY,
                titleHelper: NAVIGATION_ADMIN.ITEM_TITLE_HELPER_USER_DIRECTORY,
                visibilityHelper: NAVIGATION_ADMIN.ITEM_VISIBILITY_HELPER_USER_DIRECTORY,
            };
        default:
            return {
                contentBlockTitle: '',
                titleHelper: '',
                visibilityHelper: '',
            };
    }
};
